import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Loader2, CalendarDays, AlertCircle, Info } from 'lucide-react';
import { getLeaveQuotaSnapshot } from '../api';

const TYPE_LABEL = {
  casual: { label: 'Casual', short: 'CL', color: 'blue' },
  sick: { label: 'Sick', short: 'SL', color: 'rose' },
  annual: { label: 'Privilege', short: 'PL', color: 'purple' },
  special: { label: 'Special', short: 'SP', color: 'fuchsia' },
};

const colorClasses = {
  blue: { bar: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  rose: { bar: 'bg-rose-500', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  purple: { bar: 'bg-purple-500', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  fuchsia: { bar: 'bg-fuchsia-500', bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
  amber: { bar: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
};

export default function LeaveQuotaCard({ employeeId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await getLeaveQuotaSnapshot(employeeId);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId]);

  if (loading) {
    return (
      <Card data-testid="leave-quota-card-loading">
        <CardContent className="p-6 flex items-center justify-center text-slate-500">
          <Loader2 size={16} className="mr-2 animate-spin" /> Loading quota…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card className="border-slate-200" data-testid="leave-quota-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays size={18} className="text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-800">
              Your Leave Quota — {data.year}
            </h3>
          </div>
          {data.in_probation && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
              <AlertCircle size={11} />
              In Probation — leaves auto-marked as LWP until {data.probation_ends}
            </Badge>
          )}
        </div>

        <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-3`}>
          {Object.keys(TYPE_LABEL).map((k) => {
            const meta = TYPE_LABEL[k];
            const c = colorClasses[meta.color];
            const quota = data.quota[k] ?? 0;
            const accrued = data.accrued[k] ?? 0;
            const used = data.used[k] ?? 0;
            const available = data.available[k] ?? 0;
            const pct = quota > 0 ? Math.min(100, (used / quota) * 100) : 0;
            const isAccruedType = k === 'annual';
            return (
              <div
                key={k}
                className={`rounded-lg border ${c.border} ${c.bg} p-3`}
                data-testid={`quota-card-${k}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold uppercase ${c.text}`}>
                    {meta.label} ({meta.short})
                  </span>
                  <span className="text-[10px] text-slate-500">{used} / {quota}</span>
                </div>
                <div className={`text-2xl font-bold ${c.text}`} data-testid={`quota-available-${k}`}>
                  {available}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">available</div>
                <Progress value={pct} className="h-1.5 mt-2" />
                {isAccruedType && (
                  <div className="text-[10px] text-slate-500 mt-1.5">
                    Accrued: {accrued} of {quota} (1/mo)
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* LWP usage row */}
        <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Info size={12} />
            Leave Without Pay used this year:
          </div>
          <Badge className={`${colorClasses.amber.bg} ${colorClasses.amber.text} ${colorClasses.amber.border} border`} data-testid="quota-lwp-used">
            {data.used.unpaid ?? 0} day(s)
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
